import { LazoPaths } from "@/enums/LazoPaths";
import axios from "axios";

// localStorage keys owned by fox-typescript's AuthService (token, user, expiration, externalId).
const AUTH_STORAGE_KEYS = ["t", "t_expiration", "r", "e"];

/**
 * Purges the locally-stored auth state when the app boots on `/redirect?otc=…` — i.e. a real
 * cross-subdomain login handoff that is about to mint a fresh session via the OTC exchange.
 *
 * Why: a workspace subdomain can hold a *stale* token from a previous session. Booting "logged in"
 * with it makes background services fire requests that the backend rejects with 401 INVALID_TOKEN,
 * and HttpClientV2's global interceptor reacts by logging out and bouncing to /login — racing (and
 * beating) the in-flight OTC exchange, so the user is forced to log in twice (LS-1153). Removing
 * the stale state before any request fires eliminates the race at its source; the fresh session
 * arrives moments later from the exchange (or from the .lazo.one cookie fallback).
 *
 * Deliberately NOT AuthService.logOut(): that notifies LOGOUT observers, which navigate to /login —
 * the very bounce this purge prevents. Scoped to handoffs only (`otc` present): /redirect without
 * an OTC keeps whatever session exists.
 *
 * @returns true when stale auth state was purged.
 */
export function purgeStaleAuthOnRedirect(pathname: string, search: string): boolean {
  if (pathname !== LazoPaths.REDIRECT) return false;
  if (!new URLSearchParams(search).get("otc")) return false;

  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  delete axios.defaults.headers.common["Authorization"];
  return true;
}

/**
 * Module-boot guard: call once, as early as possible in the client bundle, so the purge runs
 * before any component effect fires an authenticated request.
 */
export function purgeStaleAuthOnRedirectBoot(): boolean {
  if (typeof window === "undefined") return false;
  return purgeStaleAuthOnRedirect(window.location.pathname, window.location.search);
}
